/**
 * FERP Portal - Rule Import Modal (DRR-058)
 *
 * Modal for importing structured regulatory rules into the
 * Regulatory Intelligence Pipeline. Accepts structured config from
 * any external LLM or human source.
 */

import { useState } from 'react';
import {
  Modal,
  Stack,
  TextInput,
  Textarea,
  Switch,
  Button,
  Alert,
  Text,
  Group,
  Badge,
  Card,
  List,
} from '@mantine/core';
import {
  IconFileImport,
  IconCheck,
  IconX,
  IconAlertTriangle,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import {
  useImportRegulatoryRulesMutation,
  type BatchImportResultFieldsFragment,
} from '@/generated/graphql';

// ============================================================================
// Types
// ============================================================================

interface RuleImportModalProps {
  opened: boolean;
  onClose: () => void;
  tenantId: string;
  onImportComplete?: () => void;
}

// ============================================================================
// Component
// ============================================================================

export function RuleImportModal({ opened, onClose, tenantId, onImportComplete }: RuleImportModalProps) {
  const [sourceId, setSourceId] = useState('');
  const [configContent, setConfigContent] = useState('');
  const [allowAutoApprove, setAllowAutoApprove] = useState(true);
  const [results, setResults] = useState<BatchImportResultFieldsFragment | null>(null);

  const [importRules, { loading: importing }] = useImportRegulatoryRulesMutation();

  const handleImport = async () => {
    if (!sourceId.trim() || !configContent.trim()) {
      notifications.show({
        title: 'Missing fields',
        message: 'Source ID and config content are required.',
        color: 'yellow',
      });
      return;
    }

    try {
      const { data } = await importRules({
        variables: {
          input: {
            tenantId,
            sourceId,
            configContent,
            allowAutoApprove,
          },
        },
      });

      if (data?.importRegulatoryRules) {
        setResults(data.importRegulatoryRules);
      }

      notifications.show({
        title: 'Import complete',
        message: 'Rules have been processed.',
        color: 'green',
        icon: <IconCheck size={16} />,
      });

      onImportComplete?.();
    } catch (err) {
      notifications.show({
        title: 'Import failed',
        message: err instanceof Error ? err.message : 'Unknown error',
        color: 'red',
        icon: <IconX size={16} />,
      });
    }
  };

  const handleClose = () => {
    setResults(null);
    onClose();
  };

  return (
    <Modal opened={opened} onClose={handleClose} size="xl" title="Import Regulatory Rules">
      <Stack gap="md">
        <TextInput
          label="Source ID"
          description="Identifier for the regulatory source document"
          placeholder="mn-dhs-245d-2026"
          value={sourceId}
          onChange={(e) => setSourceId(e.currentTarget.value)}
          required
        />

        <Textarea
          label="Config Content"
          description="Paste structured rules extracted from regulatory documents"
          placeholder="ferp_regulatory_rules:&#10;  version: '1.0'&#10;  rules:&#10;    - rule:&#10;        id: rule-001&#10;        ..."
          autosize
          minRows={10}
          maxRows={20}
          value={configContent}
          onChange={(e) => setConfigContent(e.currentTarget.value)}
          required
          styles={{ input: { fontFamily: 'monospace', fontSize: 'var(--mantine-font-size-xs)' } }}
        />

        <Switch
          label="Allow auto-approve for high-confidence rules"
          description="Rules above 90% confidence (non-life-critical) will be auto-approved"
          checked={allowAutoApprove}
          onChange={(e) => setAllowAutoApprove(e.currentTarget.checked)}
        />

        <Button
          leftSection={<IconFileImport size={16} />}
          onClick={handleImport}
          loading={importing}
          fullWidth
        >
          Import Rules
        </Button>

        {/* Results Summary */}
        {results && (
          <Card withBorder p="md">
            <Stack gap="sm">
              <Text fw={600}>Import Results</Text>
              <Group gap="md">
                <Badge color="green" size="lg" variant="light">
                  {results.importedCount} imported
                </Badge>
                {results.failedCount > 0 && (
                  <Badge color="red" size="lg" variant="light">
                    {results.failedCount} failed
                  </Badge>
                )}
                <Badge color="teal" size="lg" variant="light">
                  {results.autoApprovedCount} auto-approved
                </Badge>
                <Badge color="yellow" size="lg" variant="light">
                  {results.reviewRequiredCount} need review
                </Badge>
              </Group>

              {/* Batch-level warnings */}
              {results.warnings.length > 0 && (
                <Alert color="yellow" icon={<IconAlertTriangle size={16} />} title="Warnings">
                  <List size="sm">
                    {results.warnings.map((w, i) => (
                      <List.Item key={i}>{w}</List.Item>
                    ))}
                  </List>
                </Alert>
              )}

              {/* Per-rule errors */}
              {results.results
                .filter((r) => !r.success)
                .map((r, i) => (
                  <Alert key={i} color="red" title={`Rule ${r.ruleId ?? 'unknown'} failed`}>
                    <List size="sm">
                      {r.errors.map((e, j) => (
                        <List.Item key={j}>
                          <Text size="sm" span fw={500}>{e.field}:</Text> {e.message}
                        </List.Item>
                      ))}
                    </List>
                  </Alert>
                ))}
            </Stack>
          </Card>
        )}
      </Stack>
    </Modal>
  );
}

export default RuleImportModal;
